import { ParseError } from './errors';

/** Minimal DER TLV walker for the few structures we handle without a full ASN.1 library. */
export interface Tlv {
  tag: number;
  /** offset of the tag byte (start of the whole TLV) */
  offset: number;
  /** offset of the first content byte */
  start: number;
  /** offset one past the last content byte */
  end: number;
  /** offset one past the whole TLV (== end for DER) */
  next: number;
  content: Uint8Array;
}

export function readTlv(buf: Uint8Array, pos: number): Tlv {
  if (pos + 2 > buf.length) throw new ParseError('DER: truncated header', pos);
  const tag = buf[pos]!;
  if ((tag & 0x1f) === 0x1f) throw new ParseError('DER: multi-byte tags unsupported', pos);
  let len = buf[pos + 1]!;
  let p = pos + 2;
  if (len & 0x80) {
    const n = len & 0x7f;
    if (n === 0 || n > 4) throw new ParseError('DER: unsupported length encoding', pos + 1);
    if (p + n > buf.length) throw new ParseError('DER: truncated length', pos + 1);
    len = 0;
    for (let i = 0; i < n; i++) len = len * 256 + buf[p + i]!;
    p += n;
  }
  if (p + len > buf.length) throw new ParseError(`DER: content length ${len} exceeds buffer`, pos);
  return { tag, offset: pos, start: p, end: p + len, next: p + len, content: buf.subarray(p, p + len) };
}

/** Iterates the children of a constructed TLV (SEQUENCE / SET). */
export function* children(t: Tlv): Generator<Tlv> {
  let p = t.start;
  while (p < t.end) {
    const c = readTlv(t.content, p - t.start);
    // readTlv works on `content` with relative offsets; re-base to absolute offsets in the parent buffer.
    yield {
      tag: c.tag,
      offset: t.start + c.offset,
      start: t.start + c.start,
      end: t.start + c.end,
      next: t.start + c.next,
      content: c.content,
    };
    p = t.start + c.next;
  }
}

export const TAG = {
  INTEGER: 0x02,
  BIT_STRING: 0x03,
  OCTET_STRING: 0x04,
  NULL: 0x05,
  OID: 0x06,
  SEQUENCE: 0x30,
  SET: 0x31,
} as const;

/** Encodes a DER length. */
export function derLength(n: number): Uint8Array {
  if (n < 0x80) return new Uint8Array([n]);
  const bytes: number[] = [];
  let v = n;
  while (v > 0) {
    bytes.unshift(v & 0xff);
    v = Math.floor(v / 256);
  }
  return new Uint8Array([0x80 | bytes.length, ...bytes]);
}

export function derTlv(tag: number, content: Uint8Array): Uint8Array {
  const len = derLength(content.length);
  const out = new Uint8Array(1 + len.length + content.length);
  out[0] = tag;
  out.set(len, 1);
  out.set(content, 1 + len.length);
  return out;
}

/** Decodes an OBJECT IDENTIFIER content to dotted form. */
export function oidToString(content: Uint8Array): string {
  const parts: number[] = [];
  let v = 0;
  for (let i = 0; i < content.length; i++) {
    const b = content[i]!;
    v = v * 128 + (b & 0x7f);
    if (!(b & 0x80)) {
      if (parts.length === 0) {
        if (v < 40) parts.push(0, v);
        else if (v < 80) parts.push(1, v - 40);
        else parts.push(2, v - 80);
      } else parts.push(v);
      v = 0;
    }
  }
  return parts.join('.');
}
